import React from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { useRecorder } from "@/hooks/useRecorder";
import { toast } from "sonner";

/**
 * Microphone button that records audio via the browser and calls onTranscript(text)
 * with the Whisper transcript when the user stops recording.
 * Props: onTranscript(text), size ("sm"|"md"), testid, language
 */
export function MicButton({ onTranscript, size = "md", testid = "mic-button", language = "en", label = "Speak" }) {
  const { recording, transcribing, error, start, stop } = useRecorder({
    onTranscript,
    language,
  });

  React.useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const busy = recording || transcribing;
  const dims = size === "sm" ? "w-9 h-9" : "w-11 h-11";

  const onClick = () => {
    if (transcribing) return;
    if (recording) stop();
    else start();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-label={recording ? "Stop recording" : label}
      className={`pod-focus ${dims} inline-flex items-center justify-center rounded-full border-2 transition-colors ${
        recording
          ? "bg-red-100 border-red-500 text-red-600 animate-pulse"
          : transcribing
            ? "bg-[#FAF5EC] border-[#E4DDD3] text-[#4A525A]"
            : "bg-white hover:bg-[#FAF5EC] border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024]"
      }`}
      title={recording ? "Stop and transcribe" : "Speak"}
      disabled={busy && !recording}
    >
      {transcribing ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : recording ? (
        <MicOff className="w-4 h-4" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </button>
  );
}
